/**
 * Logs Service for Customer Portal
 * Provides methods to fetch system logs for users and app users
 */
import { getApiClient } from 'utils/apiClient';
import logger from 'utils/logger';

class LogsService {
  constructor() {
    this.apiClient = getApiClient();
  }

  /**
   * Get system logs with optional filtering
   * @param {Object} options - Filter options
   * @param {string} options.userEmail - Filter by user email (for natural users)
   * @param {string} options.appUserId - Filter by app user ID (for technical users)
   * @param {string} options.userId - Filter by user ID (for natural users or resource_id for resource-based requests)
   * @param {string} options.resourceId - Filter by resource ID (for resource-based requests)
   * @param {string} options.principalType - Filter by principal type ('natural', 'technical', or 'resource')
   * @param {string} options.routePrefix - Filter by route prefix
   * @param {number} options.statusCode - Filter by HTTP status code
   * @param {string} options.since - Filter by timestamp (ISO8601 string)
   * @param {number} options.limit - Maximum number of logs to return (default 100, max 1000)
   * @param {string} options.continuation - Continuation token for pagination
   * @returns {Promise<Object>} System logs response
   */
  async getSystemLogs(options = {}) {
    try {
      const params = new URLSearchParams();
      
      // Add filter parameters
      if (options.userEmail) {
        params.append('user_email', options.userEmail);
      }
      if (options.appUserId) {
        params.append('app_user_id', options.appUserId);
      }
      if (options.userId) {
        params.append('user_id', options.userId);
      }
      if (options.resourceId) {
        params.append('resource_id', options.resourceId);
      }
      if (options.principalType) {
        params.append('principal_type', options.principalType);
      }
      if (options.routePrefix) {
        params.append('route_prefix', options.routePrefix);
      }
      if (options.statusCode) {
        params.append('status_code', options.statusCode.toString());
      }
      if (options.since) {
        params.append('since', options.since);
      }
      if (options.limit) {
        params.append('limit', options.limit.toString());
      }
      if (options.continuation) {
        params.append('continuation', options.continuation);
      }

      // Use endpoint without /api prefix - apiClient will add it if needed
      const endpoint = `/v1/logs/system?${params.toString()}`;
      const data = await this.apiClient.apiRequest(endpoint, { method: 'GET' });
      return data;
    } catch (error) {
      logger.error('LOGS-SERVICE', 'Failed to get system logs', { error: error.message });
      throw error;
    }
  }

  /**
   * Get system logs for a specific user (natural user)
   * Returns all logs for the user regardless of principal_type.
   * @param {string} userEmail - User email address (must be valid email, not error message)
   * @param {Object} options - Additional options
   * @param {number} options.limit - Maximum number of logs to return
   * @param {string} options.continuation - Continuation token for pagination
   * @returns {Promise<Object>} System logs response
   */
  async getSystemLogsByUser(userEmail, options = {}) {
    try {
      // Validate email - don't use error messages as email
      if (!userEmail || userEmail.includes('not available') || !userEmail.includes('@')) {
        throw new Error('Invalid email address - cannot use error message as email');
      }
      
      const params = new URLSearchParams();
      
      if (options.limit) {
        params.append('limit', options.limit.toString());
      }
      if (options.continuation) {
        params.append('continuation', options.continuation);
      }
      if (options.since) {
        params.append('since', options.since);
      }
      if (options.until) {
        params.append('until', options.until);
      }

      // Use endpoint without /api prefix - apiClient will add it if needed
      const endpoint = `/v1/logs/system/user/${encodeURIComponent(userEmail)}?${params.toString()}`;
      const data = await this.apiClient.apiRequest(endpoint, { method: 'GET' });
      return data;
    } catch (error) {
      logger.error('LOGS-SERVICE', 'Failed to get system logs by user', { error: error.message, userEmail });
      throw error;
    }
  }

  /**
   * Get system logs for a specific app user (technical user)
   * @param {string} appUserId - Application user ID
   * @param {Object} options - Additional options
   * @param {number} options.limit - Maximum number of logs to return
   * @param {string} options.continuation - Continuation token for pagination
   * @returns {Promise<Object>} System logs response
   */
  async getSystemLogsByAppUser(appUserId, options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.limit) {
        params.append('limit', options.limit.toString());
      }
      if (options.continuation) {
        params.append('continuation', options.continuation);
      }
      if (options.since) {
        params.append('since', options.since);
      }
      if (options.until) {
        params.append('until', options.until);
      }

      // Use endpoint without /api prefix - apiClient will add it if needed
      const endpoint = `/v1/logs/system/app-user/${encodeURIComponent(appUserId)}?${params.toString()}`;
      const data = await this.apiClient.apiRequest(endpoint, { method: 'GET' });
      return data;
    } catch (error) {
      logger.error('LOGS-SERVICE', 'Failed to get system logs by app user', { error: error.message, appUserId });
      throw error;
    }
  }

  /**
   * Get system logs for a specific user by user ID (for technical users)
   * @param {string} userId - User ID (for technical users, this maps to app_user_id)
   * @param {Object} options - Additional options
   * @param {number} options.limit - Maximum number of logs to return
   * @param {string} options.continuation - Continuation token for pagination
   * @returns {Promise<Object>} System logs response
   */
  async getSystemLogsByUserId(userId, options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.limit) {
        params.append('limit', options.limit.toString());
      }
      if (options.continuation) {
        params.append('continuation', options.continuation);
      }
      if (options.since) {
        params.append('since', options.since);
      }
      if (options.until) {
        params.append('until', options.until);
      }

      // Use endpoint without /api prefix - apiClient will add it if needed
      const endpoint = `/v1/logs/system/user-id/${encodeURIComponent(userId)}?${params.toString()}`;
      const data = await this.apiClient.apiRequest(endpoint, { method: 'GET' });
      return data;
    } catch (error) {
      logger.error('LOGS-SERVICE', 'Failed to get system logs by user ID', { error: error.message, userId });
      throw error;
    }
  }

  /**
   * Download system logs for a specific user as CSV
   * @param {string} userEmail - User email address
   * @param {Object} filters - Filter options
   * @param {string} filters.routePrefix - Filter by route prefix
   * @param {number} filters.statusCode - Filter by HTTP status code
   * @param {string} filters.method - Filter by HTTP method
   * @param {string} filters.since - Filter by start timestamp (ISO8601 string)
   * @param {string} filters.until - Filter by end timestamp (ISO8601 string)
   * @returns {Promise<void>} Downloads CSV file
   */
  async downloadLogsCSV(userEmail, filters = {}) {
    try {
      const params = new URLSearchParams();
      
      // Add filter parameters
      if (filters.routePrefix) {
        params.append('routePrefix', filters.routePrefix);
      }
      if (filters.statusCode) {
        params.append('statusCode', filters.statusCode.toString());
      }
      if (filters.method) {
        params.append('method', filters.method);
      }
      if (filters.since) {
        params.append('since', filters.since);
      }
      if (filters.until) {
        params.append('until', filters.until);
      }

      // Construct full URL - use apiClient's baseUrl and remove /api prefix from endpoint
      // since baseUrl may already include /api
      await this.apiClient.ensureInitialized();
      const baseUrl = this.apiClient.config.baseUrl || '';
      const endpoint = `/v1/logs/system/user/${encodeURIComponent(userEmail)}/export?${params.toString()}`;
      // baseUrl already has /api for API URLs, so just append endpoint
      const fullUrl = `${baseUrl}${endpoint}`;
      
      // Make request to get CSV blob
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.apiClient.getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Get the CSV content as blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header or create default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `activity_logs_${userEmail.replace('@', '_at_')}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      logger.info('LOGS-SERVICE', 'CSV download initiated', { userEmail });
    } catch (error) {
      logger.error('LOGS-SERVICE', 'Failed to download logs CSV', { error: error.message, userEmail });
      throw error;
    }
  }

  /**
   * Format log entry for display
   * @param {Object} log - Log entry from API
   * @returns {Object} Formatted log entry
   */
  formatLogEntry(log) {
    return {
      id: log.id,
      timestamp: log.timestamp,
      route: log.route,
      method: log.method,
      statusCode: log.status_code,
      principalType: log.principal_type,
      userEmail: log.user_email,
      appUserId: log.app_user_id,
      resourceType: log.resource_type,
      resourceId: log.resource_id,
      metadata: log.metadata,
      // Computed fields for display
      formattedTimestamp: this.formatTimestamp(log.timestamp),
      statusColor: this.getStatusColor(log.status_code),
      methodColor: this.getMethodColor(log.method),
      principalTypeLabel: this.getPrincipalTypeLabel(log.principal_type)
    };
  }

  /**
   * Format timestamp for display
   * @param {string} timestamp - ISO8601 timestamp
   * @returns {string} Formatted timestamp
   */
  formatTimestamp(timestamp) {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch (error) {
      return timestamp;
    }
  }

  /**
   * Get status color for HTTP status code
   * @param {number} statusCode - HTTP status code
   * @returns {string} CSS color class
   */
  getStatusColor(statusCode) {
    if (statusCode >= 200 && statusCode < 300) return 'status-success';
    if (statusCode >= 300 && statusCode < 400) return 'status-redirect';
    if (statusCode >= 400 && statusCode < 500) return 'status-client-error';
    if (statusCode >= 500) return 'status-server-error';
    return 'status-unknown';
  }

  /**
   * Get method color for HTTP method
   * @param {string} method - HTTP method
   * @returns {string} CSS color class
   */
  getMethodColor(method) {
    switch (method?.toUpperCase()) {
      case 'GET': return 'method-get';
      case 'POST': return 'method-post';
      case 'PUT': return 'method-put';
      case 'DELETE': return 'method-delete';
      case 'PATCH': return 'method-patch';
      default: return 'method-unknown';
    }
  }

  /**
   * Get system logs for a specific resource (resource-based requests)
   * @param {string} resourceId - Resource ID
   * @param {Object} options - Additional options
   * @param {number} options.limit - Maximum number of logs to return
   * @param {string} options.continuation - Continuation token for pagination
   * @returns {Promise<Object>} System logs response
   */
  async getSystemLogsByResource(resourceId, options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.limit) {
        params.append('limit', options.limit.toString());
      }
      if (options.continuation) {
        params.append('continuation', options.continuation);
      }

      // Use endpoint without /api prefix - apiClient will add it if needed
      const endpoint = `/v1/logs/system?resource_id=${encodeURIComponent(resourceId)}&principal_type=resource&${params.toString()}`;
      const data = await this.apiClient.apiRequest(endpoint, { method: 'GET' });
      return data;
    } catch (error) {
      logger.error('LOGS-SERVICE', 'Failed to get system logs by resource', { error: error.message, resourceId });
      throw error;
    }
  }

  /**
   * Get principal type label
   * @param {string} principalType - Principal type
   * @returns {string} Human-readable label
   */
  getPrincipalTypeLabel(principalType) {
    switch (principalType) {
      case 'natural': return 'User';
      case 'technical': return 'App User';
      case 'resource': return 'Resource';
      default: return principalType;
    }
  }
}

// Export singleton instance
const logsServiceInstance = new LogsService();
export default logsServiceInstance;
